import { type Bar, type LibrarySymbolInfo, type ResolutionString, type SubscribeBarsCallback } from 'charting_library';

import { type IMediator } from '#/core/mediator';

import { type IUrlParamsService, UrlParamsName } from '#/services/url-params';

import { isTickTimeframe } from '#/shared/utils/is-tick-timeframe';
import { timeframeToMilliseconds } from '#/shared/utils/timeframe-to-milliseconds';
import { udfResponseToBarsResult } from '#/shared/utils/udf-response-to-bars-result';

import { type CandlesUdfResponse, type TicksUdfResponse } from '#/types/udf.types';

import { SERVICES_TYPES } from '#/services-types';

import { type IApiService } from '../api';
import { type ILogService } from '../log';
import { type IServerTimeService } from '../server-time';

import { type DataSubscribers, type IIntervalBarUpdateService } from './interval-bar-update.types';

export class IntervalBarUpdateService implements IIntervalBarUpdateService {
  readonly #serviceName = 'IntervalBarUpdateService';

  get #apiService() {
    return this._mediator.get<IApiService>(SERVICES_TYPES.ApiService);
  }
  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }
  get #serverTimeService() {
    return this._mediator.get<IServerTimeService>(SERVICES_TYPES.ServerTimeService);
  }
  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  #subscribers: DataSubscribers = {};
  #historyUpdateInterval = 30 * 1000;
  #requestsPending = 0;

  constructor(private readonly _mediator: IMediator) {}

  subscribe({
    listenerGuid,
    symbolInfo,
    resolution,
    newDataCallback,
  }: {
    listenerGuid: string;
    symbolInfo: LibrarySymbolInfo;
    resolution: ResolutionString;
    newDataCallback: SubscribeBarsCallback;
  }) {
    if (Object.hasOwn(this.#subscribers, listenerGuid)) {
      this.#logService.info(this.#serviceName, `Already has subscriber with id=${listenerGuid}.`);

      return;
    }

    this.#subscribers[listenerGuid] = {
      lastBarTime: null,
      listener: newDataCallback,
      resolution: resolution,
      symbolInfo: symbolInfo,
    };

    this.#logService.info(this.#serviceName, `Subscribed for #${listenerGuid} - {${symbolInfo.name}, ${resolution}}.`);
  }

  run() {
    this.#logService.info(this.#serviceName, `Interval was run.`);

    const sourceParameterFromUrl = this.#urlParamsService.getParamByName(UrlParamsName.Ds) || undefined;

    if (!sourceParameterFromUrl) {
      this.#logService.warn(this.#serviceName, 'Missing "ds" parameter for history request in page URL', {
        sourceParameterFromUrl,
      });
    }

    setInterval(async () => {
      if (this.#requestsPending > 0) return;

      this.#requestsPending = 0;

      for (const listenerGuid in this.#subscribers) {
        this.#requestsPending += 1;

        const subscriptionRecord = this.#subscribers[listenerGuid];
        const { symbolInfo, resolution } = subscriptionRecord;

        let udfResponse: CandlesUdfResponse | TicksUdfResponse;

        if (isTickTimeframe(resolution)) {
          const to = this.#serverTimeService.now();
          const from = to - this.#historyUpdateInterval;

          udfResponse = (
            await this.#apiService.ticksHistory({
              urlParams: {
                symbol: symbolInfo.ticker || '',
                from,
                to,
                resolution,
                countback: 2,
                source: sourceParameterFromUrl,
              },
            })
          ).data;
        } else {
          const to = Math.round(this.#serverTimeService.now() / 1000);
          const from = to - Math.round((timeframeToMilliseconds(resolution) as number) / 1000);

          udfResponse = (
            await this.#apiService.candlesHistory({
              urlParams: {
                symbol: symbolInfo.ticker || '',
                from,
                to,
                resolution: resolution,
                countback: 2,
                source: sourceParameterFromUrl,
              },
            })
          ).data;
        }

        const { bars } = udfResponseToBarsResult(udfResponse);

        if (bars.length === 0) {
          this.#requestsPending -= 1;

          return;
        }

        const lastBar = bars.at(-1) as Bar;

        if (subscriptionRecord.lastBarTime !== null && lastBar.time < subscriptionRecord.lastBarTime) {
          this.#requestsPending -= 1;

          return;
        }

        const isNewBar = subscriptionRecord.lastBarTime !== null && lastBar.time > subscriptionRecord.lastBarTime;

        // Pulse updating may miss some trades data (ie, if pulse period = 10 seconds and new bar is started 5 seconds
        // later after the last update, the old bar's last 5 seconds trades will be lost). Thus, at fist we should
        // broadcast old bar updates when it's ready.
        if (isNewBar) {
          if (bars.length < 2) {
            this.#logService.warn(
              this.#serviceName,
              'Not enough bars in history for proper pulse update. Need at least 2.',
              { udfResponse }
            );

            this.#requestsPending -= 1;

            return;
          }

          const previousBar = bars.at(-2) as Bar;

          subscriptionRecord.listener(previousBar);
        }

        subscriptionRecord.lastBarTime = lastBar.time;
        subscriptionRecord.listener(lastBar);

        this.#requestsPending -= 1;
      }
    }, this.#historyUpdateInterval);
  }

  unsubscribe(listenerGuid: string) {
    delete this.#subscribers[listenerGuid];

    this.#logService.info(this.#serviceName, `Unsubscribed for #${listenerGuid}.`);
  }
}
